import { useState, useEffect } from "react";
import axios from "axios";
import { FileText, Shield, Save, Loader2, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LegalPagesManager() {
  const [activeTab, setActiveTab] = useState("regulamin");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [regulaminData, setRegulaminData] = useState({ title: "Regulamin", content: "" });
  const [privacyData, setPrivacyData] = useState({ title: "Polityka prywatności", content: "" });

  useEffect(() => {
    const fetchPages = async () => {
      setLoading(true);
      try {
        const [regRes, privRes] = await Promise.all([
          axios.get(`${API}/admin/legal/regulamin`),
          axios.get(`${API}/admin/legal/polityka-prywatnosci`)
        ]);
        setRegulaminData(regRes.data);
        setPrivacyData(privRes.data);
      } catch (err) {
        console.error("Błąd pobierania stron prawnych:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPages();
  }, []);

  const handleSave = async (pageType) => {
    setSaving(true);
    const data = pageType === "regulamin" ? regulaminData : privacyData;
    
    try {
      await axios.put(`${API}/admin/legal/${pageType}`, data);
      toast.success("Zapisano pomyślnie!");
    } catch (err) {
      toast.error("Błąd zapisu: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const renderEditor = (pageType, data, setData) => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor={`title-${pageType}`}>Tytuł strony</Label>
        <Input
          id={`title-${pageType}`}
          value={data.title}
          onChange={(e) => setData({ ...data, title: e.target.value })}
          placeholder="Tytuł strony"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor={`content-${pageType}`}>Treść (HTML)</Label>
        <textarea
          id={`content-${pageType}`}
          value={data.content}
          onChange={(e) => setData({ ...data, content: e.target.value })}
          placeholder="<h2>Tytuł sekcji</h2><p>Treść...</p>"
          className="w-full min-h-[400px] p-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
        />
        <p className="text-xs text-slate-500">
          Możesz używać tagów HTML: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;a href=""&gt;
        </p>
      </div>

      {data.last_updated && (
        <p className="text-sm text-slate-400">
          Ostatnia aktualizacja: {new Date(data.last_updated).toLocaleString('pl-PL')}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button 
          onClick={() => handleSave(pageType)} 
          disabled={saving}
          className="gap-2"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Zapisz zmiany
        </Button>
        
        <a 
          href={`/${pageType}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline"
        >
          Podgląd strony →
        </a>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          Strony prawne
        </CardTitle>
        <CardDescription>
          Zarządzaj treścią Regulaminu i Polityki prywatności
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="regulamin" className="gap-2">
              <FileText className="w-4 h-4" />
              Regulamin
            </TabsTrigger>
            <TabsTrigger value="polityka-prywatnosci" className="gap-2">
              <Shield className="w-4 h-4" />
              Polityka prywatności
            </TabsTrigger>
          </TabsList>

          <TabsContent value="regulamin">
            {renderEditor("regulamin", regulaminData, setRegulaminData)}
          </TabsContent>

          <TabsContent value="polityka-prywatnosci">
            {renderEditor("polityka-prywatnosci", privacyData, setPrivacyData)}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
